/**
 * Segments an assistant markdown stream into typed render blocks.
 *
 * Uses marked's lexer (no rendering) to split top-level tokens by
 * structural intent. Tables get their own block so the dispatcher can
 * route them to a typed renderer; everything else (paragraph, heading,
 * list, code, quote, html) coalesces into prose blocks rendered by the
 * existing MarkdownView fallback.
 *
 * Streaming-safe: re-segment on every source change. The lexer is
 * microsecond-fast and partial tables degrade to paragraph until the
 * header/separator/row triplet is complete.
 */
import { Lexer, type Token } from "marked";

import type { RenderBlock, StructuralIntent } from "./intent";

const PROSE_KINDS: ReadonlySet<string> = new Set([
  "paragraph",
  "heading",
  "list",
  "code",
  "blockquote",
  "hr",
  "html",
  "space",
  "text",
  "def",
]);

function intentOf(token: Token): StructuralIntent | "skip" {
  if (token.type === "table") return "table";
  if (PROSE_KINDS.has(token.type)) return "prose";
  return "skip";
}

export function segmentBlocks(source: string): RenderBlock[] {
  if (!source) return [];
  const tokens = Lexer.lex(source);
  const blocks: RenderBlock[] = [];
  let buffer = "";

  const flushProse = () => {
    if (buffer.trim().length > 0) {
      blocks.push({ intent: "prose", raw: buffer });
    }
    buffer = "";
  };

  for (const token of tokens) {
    const intent = intentOf(token);
    if (intent === "table") {
      flushProse();
      blocks.push({ intent: "table", raw: token.raw });
      continue;
    }
    if (intent === "prose") {
      buffer += token.raw;
    }
  }
  flushProse();
  return blocks;
}
